import { useEffect, useState, useMemo, useRef, useTransition } from 'react';
import debounce from 'lodash/debounce';
import { useQueryLoader } from 'react-relay';
import useIsAdminHubAIEnabled from '@atlassian/jira-platform-react-hooks-use-is-admin-hub-ai-enabled/src/controllers/index.tsx';
import { useProductName } from '@atlassian/jira-tenant-context-controller/src/components/product-name/index.tsx';
import type {
	FormFieldValues,
	GeneralFieldValue,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { useIssueCreateModalWithPrefillingForIssueCreateEmbed } from '@atlassian/jira-new-issue-create-trigger/src/utils/use-issue-create-modal-prefiling-for-issue-create-embed/index.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useOpenIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import type { RelatedIssue } from '@atlassian/jira-ai-related-issues/src/controllers/context.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import AiRelatedIssuesIssueLinkTypesQuery, {
	type AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery,
} from '@atlassian/jira-relay/src/__generated__/AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { getProductForAdminHubAi, transformTheIssueDataToGICFormat } from '../utils.tsx';

type Props = {
	getFormState: () => FormFieldValues;
	setFieldValue: (name: string, value: GeneralFieldValue) => void;
	selectedProjectId?: string;
};

export type LinkRelatedIssuePayload = {
	label: string;
	value: string;
	id: string;
	iconUrl: string;
};

// Shared hook for common similar work items logic
export const useSimilarWorkItemsCommon = (props: Props) => {
	const summary = props.getFormState()?.values?.summary || '';
	const linkedWorkItems = props.getFormState()?.values?.issuelinks?.issues || [];
	const linkedWorkItemValues = linkedWorkItems.map(
		(linkedWorkItem: LinkRelatedIssuePayload) => linkedWorkItem.value,
	);
	const selectedProjectId = props.selectedProjectId || '';
	const isLinkedWorkItemFieldPresent = props.getFormState()?.values?.issuelinks || null;

	const [showComponent, setShowComponent] = useState(false);
	const [debouncedSummary, setDebouncedSummary] = useState('');
	const prevSummaryRef = useRef(summary);

	const productName = useProductName();
	const adminHubAiProductName = getProductForAdminHubAi(productName);

	const { data: isAdminHubAiEnabled, fetchAIEnablement } =
		useIsAdminHubAIEnabled(adminHubAiProductName);

	const getPrefilledIssueCreateProps = useIssueCreateModalWithPrefillingForIssueCreateEmbed();
	const openIssueCreateModal = useOpenIssueCreateModal();

	const [aiRelatedIssuesIssueLinkTypesPreloadedQuery, loadQuery] =
		useQueryLoader<AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery>(
			AiRelatedIssuesIssueLinkTypesQuery,
		);
	const cloudId = useCloudId();

	useEffect(() => {
		if (fg('remove-issue-link-types-dfo')) {
			loadQuery({
				cloudId,
			});
		}
	}, [cloudId, loadQuery]);

	// check if AI is enabled for the selected project/product
	useEffect(() => {
		fetchAIEnablement();
	}, [fetchAIEnablement]);

	const [isPending, startTransition] = useTransition();

	const debouncedUpdate = useMemo(
		() =>
			debounce((value) => {
				if (fg('thor_gic_similar_issues_relay_migration')) {
					startTransition(() => {
						setDebouncedSummary(value);
					});
				} else {
					setDebouncedSummary(value);
				}
				if (value) {
					setShowComponent(true);
				} else {
					setShowComponent(false);
				}
			}, 1000),
		[],
	);

	useEffect(() => {
		const trimmedSummary = String(summary).trim();

		if (trimmedSummary !== prevSummaryRef.current) {
			debouncedUpdate(trimmedSummary);
			prevSummaryRef.current = trimmedSummary;
		}
		return () => {
			debouncedUpdate.cancel();
		};
	}, [summary, debouncedUpdate]);

	const prefillIssueCreateModalWithData = async (transformedPayload: Record<string, unknown>) => {
		const { issueCreatePayload, prefilledCustomFields, ecosystemCallback } =
			await getPrefilledIssueCreateProps(transformedPayload);

		openIssueCreateModal({
			triggerPointKey: 'global-create-button',
			payload: {
				...issueCreatePayload,
			},
			prefilledCustomFields,
			ecosystemCallback,
		});
	};

	const onCopyButtonClick = async (issueKey: string): Promise<void> => {
		try {
			const response = await performGetRequest(`/rest/api/2/issue/${issueKey}`);
			const transformedPayload = transformTheIssueDataToGICFormat(response?.fields);
			prefillIssueCreateModalWithData(transformedPayload);
		} catch (copyError: unknown) {
			fireErrorAnalytics({
				error: copyError instanceof Error ? copyError : new Error('Unknown error'),
				meta: {
					id: 'getIssueFieldsSimilarWorkItemsInGIC',
					packageName: '@atlassian/jira-issue-create',
					teamName: 'gravity-ai',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	const onLinkIssueButtonClick = (relatedIssue: RelatedIssue): void => {
		const linkRelatedIssuePayload: LinkRelatedIssuePayload = {
			label: relatedIssue.issueKey,
			value: relatedIssue.issueKey,
			id: relatedIssue.id,
			iconUrl: relatedIssue.issueTypeIconUrl,
		};

		const currentLinking = props.getFormState()?.values?.issuelinks.linkType || null;
		const currentLinkedIssues = props.getFormState()?.values?.issuelinks.issues || [];
		if (currentLinking) {
			props.setFieldValue('issuelinks', {
				linkType: currentLinking,
				issues: [...currentLinkedIssues, linkRelatedIssuePayload],
			});
		}
	};

	return {
		summary,
		linkedWorkItemValues,
		selectedProjectId,
		isLinkedWorkItemFieldPresent,
		showComponent,
		debouncedSummary,
		isAdminHubAiEnabled,
		aiRelatedIssuesIssueLinkTypesPreloadedQuery,
		onCopyButtonClick,
		onLinkIssueButtonClick,
		isPending: fg('thor_gic_similar_issues_relay_migration') ? isPending : undefined,
	};
};
